'use client';

import { useMutation } from '@tanstack/react-query';
import { isAmbiguousLandingError } from '../../../tx-engine/watch/index.js';
import { trackTradeAttempted, trackTxException, trackTxFailure, trackTxSuccess } from './helpers/telemetry.js';
import { generateTraceId } from './helpers/trace.js';
/** Every product trade enters here once, before building or submitting any transaction legs. */
export function useTradeMutation(deps, instruction, options) {
  return useMutation({
    ...options,
    mutationFn: dispatchTrade
  });
  async function dispatchTrade(params) {
    const traceId = generateTraceId();
    const network = deps.getActiveNetwork();
    const startedAt = Date.now();
    let emittedOutcome = false;
    let submitted = false;
    const connectionScope = {};
    const ctx = {
      traceId,
      instruction,
      walletType: params.activeWallet.type,
      walletAddress: params.activeWallet.address,
      marketAddress: params.marketAddress,
      outcomeIndex: 'fromOutcomeIndex' in params ? params.fromOutcomeIndex : params.outcomeIndex,
      latencyMs: 0,
      ...(params.platformFeeD6 === undefined ? {} : {
        platformFeeD6: params.platformFeeD6
      }),
      tradeNotionalD6: 'walletDebitD6' in params ? params.walletDebitD6.toString() : 'grossQuoteAmountD6' in params ? params.grossQuoteAmountD6.toString() : params.sellGrossQuoteOutputD6.toString()
    };
    const attemptDeps = {
      ...deps,
      getActiveNetwork: () => network,
      submitTx: async (build, options_0) => {
        if (!submitted && deps.getActiveNetwork() !== network) throw new Error('Trade network changed before submission');
        deps.recordTradeSubmission?.({
          traceId,
          submissionTraceId: options_0.traceId ?? traceId,
          isFinalLeg: instruction !== 'swap' || options_0.instructionName !== 'sell'
        });
        submitted = true;
        return deps.submitTx(build, {
          ...options_0,
          connectionScope
        });
      },
      emitEvent: (name, payload) => {
        const isOutcome = ['trade_success', 'trade_error', 'trade_cancelled', 'trade_unknown'].includes(name);
        if (isOutcome && emittedOutcome) return;
        if (isOutcome) emittedOutcome = true;
        const eventName = name === 'trade_error' && payload.errorCode === 'TX_CANCELLED' ? 'trade_cancelled' : name === 'trade_error' && submitted && typeof payload.errorCode === 'string' && isAmbiguousLandingError(payload.errorCode) ? 'trade_unknown' : name;
        deps.emitEvent(eventName, {
          ...payload,
          network,
          traceId
        });
      }
    };
    trackTradeAttempted(attemptDeps, ctx);
    try {
      // Resolve transaction resources once, before asynchronous work or a network toggle.
      const connection = deps.getConnection();
      const usdcMint = deps.getUsdcMint();
      attemptDeps.getConnection = () => connection;
      attemptDeps.getUsdcMint = () => usdcMint;
      const result = await options.mutationFn(params, traceId, attemptDeps);
      ctx.latencyMs = Date.now() - startedAt;
      if (!emittedOutcome) {
        if (result.ok) trackTxSuccess(attemptDeps, ctx, result.signature);else if (result.error.code === 'TX_CANCELLED') attemptDeps.emitEvent('trade_cancelled', {
          ...ctx,
          network,
          errorCode: 'TX_CANCELLED'
        });else trackTxFailure(attemptDeps, ctx, result.error);
      }
      return result;
    } catch (error) {
      ctx.latencyMs = Date.now() - startedAt;
      if (!emittedOutcome) trackTxException(attemptDeps, ctx, error);
      throw error;
    }
  }
}